import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Bot, Settings, MessageCircle, Users, TrendingUp, Power,
  Save, Loader2, Clock, Bell, Mail, Phone, Plus, Trash2,
  ChevronRight, Sparkles, Check
} from 'lucide-react';
import Navbar from '../../components/layout/Navbar';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function CustomerBotPage() {
  const [botConfig, setBotConfig] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [selectedConversation, setSelectedConversation] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    bot_name: '',
    greeting_message: '',
    personality: 'profesional',
    auto_respond: true,
    notify_on_lead: true,
    notify_email: '',
    notify_whatsapp: '',
    out_of_hours_message: '',
    custom_faqs: []
  });
  
  // New FAQ state
  const [newFaq, setNewFaq] = useState({ question: '', answer: '' });

  const token = localStorage.getItem('upim_token');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [configRes, statsRes, convsRes] = await Promise.all([
        axios.get(`${API_URL}/api/customer-bot/config`, { headers }),
        axios.get(`${API_URL}/api/customer-bot/stats`, { headers }),
        axios.get(`${API_URL}/api/customer-bot/conversations`, { headers })
      ]);
      
      if (configRes.data.bot) {
        setBotConfig(configRes.data.bot);
        setFormData({
          bot_name: configRes.data.bot.bot_name || '',
          greeting_message: configRes.data.bot.greeting_message || '',
          personality: configRes.data.bot.personality || 'profesional',
          auto_respond: configRes.data.bot.auto_respond ?? true,
          notify_on_lead: configRes.data.bot.notify_on_lead ?? true,
          notify_email: configRes.data.bot.notify_email || '',
          notify_whatsapp: configRes.data.bot.notify_whatsapp || '',
          out_of_hours_message: configRes.data.bot.out_of_hours_message || '',
          custom_faqs: configRes.data.bot.custom_faqs || []
        });
      }
      
      setStats(statsRes.data);
      setConversations(convsRes.data.conversations || []);
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/customer-bot/config`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Configuración guardada');
      loadData();
    } catch (err) {
      alert('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const toggleBot = async () => {
    try {
      const res = await axios.post(`${API_URL}/api/customer-bot/config/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert(res.data.message);
      loadData();
    } catch (err) {
      alert('Error al cambiar estado');
    }
  };

  const addFaq = () => {
    if (!newFaq.question || !newFaq.answer) return;
    setFormData(prev => ({
      ...prev,
      custom_faqs: [...prev.custom_faqs, { ...newFaq }]
    }));
    setNewFaq({ question: '', answer: '' });
  };

  const removeFaq = (index) => {
    setFormData(prev => ({
      ...prev,
      custom_faqs: prev.custom_faqs.filter((_, i) => i !== index)
    }));
  };

  const loadConversation = async (convId) => {
    try {
      const res = await axios.get(`${API_URL}/api/customer-bot/conversations/${convId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedConversation(res.data);
    } catch (err) {
      console.error('Error loading conversation:', err);
    }
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <Bot className="w-8 h-8 text-indigo-600" />
                Bot de Atención a Clientes
              </h1>
              <p className="text-gray-500 mt-1">
                IA que responde consultas de compradores 24/7
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={toggleBot}
                className={`flex items-center gap-2 px-5 py-2 rounded-lg font-medium ${
                  botConfig?.is_active
                    ? 'bg-red-100 text-red-700 hover:bg-red-200'
                    : 'bg-green-100 text-green-700 hover:bg-green-200'
                }`}
              >
                <Power className="w-4 h-4" />
                {botConfig?.is_active ? 'Desactivar' : 'Activar'}
              </button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {[
              { label: 'Conversaciones', value: stats.total_conversations || 0, icon: MessageCircle, color: 'indigo' },
              { label: 'Leads Capturados', value: stats.total_leads_captured || 0, icon: Users, color: 'green' },
              { label: 'Bot Activo', value: stats.bot_active ? 'Sí' : 'No', icon: Bot, color: stats.bot_active ? 'green' : 'gray' },
              { label: 'Pendientes', value: stats.conversations_by_status?.active || 0, icon: Clock, color: 'amber' },
            ].map((stat, i) => (
              <div key={i} className="bg-white rounded-xl p-4 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 bg-${stat.color}-100 rounded-lg flex items-center justify-center`}>
                    <stat.icon className={`w-5 h-5 text-${stat.color}-600`} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                    <p className="text-sm text-gray-500">{stat.label}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-6 overflow-x-auto">
            {[
              { id: 'dashboard', name: 'Conversaciones', icon: MessageCircle },
              { id: 'settings', name: 'Configuración', icon: Settings },
              { id: 'faqs', name: 'FAQs', icon: Sparkles },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-100'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.name}
              </button>
            ))}
          </div>

          {/* Content */}
          {activeTab === 'dashboard' && (
            <div className="grid md:grid-cols-3 gap-6">
              {/* Conversations List */}
              <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="p-4 border-b">
                  <h3 className="font-semibold text-gray-900">Conversaciones Recientes</h3>
                </div>
                <div className="divide-y max-h-[600px] overflow-y-auto">
                  {conversations.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                      <MessageCircle className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>Sin conversaciones aún</p>
                    </div>
                  ) : (
                    conversations.map(conv => (
                      <button
                        key={conv.id}
                        onClick={() => loadConversation(conv.id)}
                        className={`w-full p-4 text-left hover:bg-gray-50 transition-colors ${
                          selectedConversation?.id === conv.id ? 'bg-indigo-50' : ''
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-gray-900">
                              {conv.customer_name || 'Cliente anónimo'}
                            </p>
                            <p className="text-sm text-gray-500">
                              {conv.message_count} mensajes
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            {conv.is_lead_captured && (
                              <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                                Lead
                              </span>
                            )}
                            <ChevronRight className="w-4 h-4 text-gray-400" />
                          </div>
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </div>

              {/* Conversation Detail */}
              <div className="md:col-span-2 bg-white rounded-xl shadow-sm overflow-hidden">
                {selectedConversation ? (
                  <>
                    <div className="p-4 border-b bg-gray-50">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-semibold text-gray-900">
                            {selectedConversation.customer_name || 'Cliente anónimo'}
                          </h3>
                          {selectedConversation.customer_phone && (
                            <p className="text-sm text-gray-500 flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {selectedConversation.customer_phone}
                            </p>
                          )}
                        </div>
                        {selectedConversation.is_lead_captured && (
                          <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm flex items-center gap-1">
                            <Check className="w-3 h-3" />
                            Lead capturado
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="p-4 max-h-[500px] overflow-y-auto space-y-3">
                      {selectedConversation.messages?.map((msg, i) => (
                        <div
                          key={i}
                          className={`flex ${msg.role === 'user' ? 'justify-start' : 'justify-end'}`}
                        >
                          <div className={`max-w-[80%] p-3 rounded-2xl ${
                            msg.role === 'user'
                              ? 'bg-gray-100 text-gray-800'
                              : 'bg-indigo-600 text-white'
                          }`}>
                            <p className="text-sm">{msg.content}</p>
                            <p className={`text-xs mt-1 ${
                              msg.role === 'user' ? 'text-gray-500' : 'text-indigo-200'
                            }`}>
                              {new Date(msg.timestamp).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="p-12 text-center text-gray-500">
                    <MessageCircle className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <p>Selecciona una conversación para ver los detalles</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'settings' && (
            <div className="bg-white rounded-xl p-6 shadow-sm space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nombre del Bot</label>
                  <input
                    type="text"
                    value={formData.bot_name}
                    onChange={(e) => setFormData({ ...formData, bot_name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Asistente Virtual"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Personalidad</label>
                  <select
                    value={formData.personality}
                    onChange={(e) => setFormData({ ...formData, personality: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="profesional">Profesional</option>
                    <option value="amigable">Amigable</option>
                    <option value="formal">Formal</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mensaje de bienvenida</label>
                <textarea
                  value={formData.greeting_message}
                  onChange={(e) => setFormData({ ...formData, greeting_message: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="¡Hola! Soy tu asistente virtual..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mensaje fuera de horario</label>
                <textarea
                  value={formData.out_of_hours_message}
                  onChange={(e) => setFormData({ ...formData, out_of_hours_message: e.target.value })}
                  rows={2}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Gracias por contactarnos, te responderemos pronto..."
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Mail className="w-4 h-4 inline mr-1" />
                    Email para notificaciones
                  </label>
                  <input
                    type="email"
                    value={formData.notify_email}
                    onChange={(e) => setFormData({ ...formData, notify_email: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Phone className="w-4 h-4 inline mr-1" />
                    WhatsApp para notificaciones
                  </label>
                  <input
                    type="tel"
                    value={formData.notify_whatsapp}
                    onChange={(e) => setFormData({ ...formData, notify_whatsapp: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>

              <div className="space-y-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.auto_respond}
                    onChange={(e) => setFormData({ ...formData, auto_respond: e.target.checked })}
                    className="w-5 h-5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-gray-700">Responder automáticamente con IA</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.notify_on_lead}
                    onChange={(e) => setFormData({ ...formData, notify_on_lead: e.target.checked })}
                    className="w-5 h-5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-gray-700">Notificarme cuando se capture un lead</span>
                </label>
              </div>

              <button
                onClick={saveConfig}
                disabled={saving}
                className="flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Guardar Configuración
              </button>
            </div>
          )}

          {activeTab === 'faqs' && (
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-4">Preguntas Frecuentes Personalizadas</h3>
              <p className="text-sm text-gray-500 mb-6">
                Agrega respuestas predefinidas para que el bot las use al atender clientes
              </p>

              {/* Add new FAQ */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="space-y-3">
                  <input
                    type="text"
                    value={newFaq.question}
                    onChange={(e) => setNewFaq({ ...newFaq, question: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="¿Cuál es la pregunta frecuente?"
                  />
                  <textarea
                    value={newFaq.answer}
                    onChange={(e) => setNewFaq({ ...newFaq, answer: e.target.value })}
                    rows={2}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Respuesta del bot..."
                  />
                  <button
                    onClick={addFaq}
                    className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                  >
                    <Plus className="w-4 h-4" />
                    Agregar FAQ
                  </button>
                </div>
              </div>

              {/* FAQs List */}
              <div className="space-y-3">
                {formData.custom_faqs.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Sparkles className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>Sin preguntas frecuentes aún</p>
                  </div>
                ) : (
                  formData.custom_faqs.map((faq, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <p className="font-medium text-gray-900 mb-1">P: {faq.question}</p>
                          <p className="text-sm text-gray-600">R: {faq.answer}</p>
                        </div>
                        <button
                          onClick={() => removeFaq(index)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {formData.custom_faqs.length > 0 && (
                <button
                  onClick={saveConfig}
                  disabled={saving}
                  className="mt-6 flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Guardar FAQs
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
